import {AddUserType} from "../@const"
import ApiProjectUser from "../../../gd/api/project-user"
import ApiTask from "../../../gd/api/task"
import ApiBulkActions from "../../../gd/api/bulk-actions"
import {AddUser_Manager} from "../model/manager"
import UsersToAdd from "../model/collection"
import UserToAdd from "../model/add-user"
import Task from "../../../model2/task/task"
import Workspace from "../../../model2/space/workspaces/workspace"
import ProjectSession from "../../../model2/project/project-session"
import Chat from "../../../model2/chat/chat/chat"
import ApiChat from "../../../gd/api/chat"
import {ProjectAccess} from "../../../gd/const/project"

// const debug = "AddUser_createUsers"
const debug = false

export default async function AddUser_createUsers(manager?: AddUser_Manager) {

    debug && console.log(debug, 'process', manager)

    if (!manager) {
        console.warn('AddUser_createUsers: no manager is passed')
        return
    }

    switch (manager.type) {
        case AddUserType.WORKSPACE_USER:
            if (!_.isEmpty(manager.companyUsersToAdd.selected)) {
                await AddUser_createWorkspaceUsers(manager.workspace, manager.companyUsersToAdd)
            }
            break

        case AddUserType.PROJECT_USER:
            if (manager.workspace) {
                if (!_.isEmpty(manager.companyUsersToAdd.selected)) {
                    // here we need create workspace users and then project users
                    await AddUser_createWorkspaceUsers(manager.workspace, manager.companyUsersToAdd)
                    await AddUser_createProjectUsers(manager.project, manager.companyUsersToAdd)
                }

                if (manager.workspaceUsersToAdd && !_.isEmpty(manager.workspaceUsersToAdd.selected)) {
                    await AddUser_createProjectUsers(manager.project, manager.workspaceUsersToAdd)
                }
            } else {
                if (!_.isEmpty(manager.companyUsersToAdd.selected)) {
                    await AddUser_createProjectUsers(manager.project, manager.companyUsersToAdd)
                }
            }
            break

        case AddUserType.TASK_USER:

            if (manager.tasks) {
                // Bulk task add. The client knows per-user which workspaces /
                // projects the user is still missing from and ships one
                // /bulk/tasks/users/add request whose `users` array carries
                // each user's own project-grant diff. Sending everything in a
                // single request lets the backend emit one
                // onTaskUsersChanged per touched task with the fully-updated
                // user list, so no last-write-wins races between per-user
                // requests. Backend silently skips IDs not derived from the
                // selected tasks.
                await AddUser_createBulkTaskUsers(manager)
                break
            }

            if (manager.workspace) {
                if (!_.isEmpty(manager.companyUsersToAdd.selected)) {
                    // here we need create workspace users and then project users
                    await AddUser_createWorkspaceUsers(manager.workspace, manager.companyUsersToAdd)
                    await AddUser_createProjectUsers(manager.project, manager.companyUsersToAdd)
                    await AddUser_createTaskUsers(manager.task, manager.companyUsersToAdd)
                }

                if (manager.workspaceUsersToAdd && !_.isEmpty(manager.workspaceUsersToAdd.selected)) {
                    await AddUser_createProjectUsers(manager.project, manager.workspaceUsersToAdd)
                    await AddUser_createTaskUsers(manager.task, manager.workspaceUsersToAdd)
                }

                if (manager.projectUsersToAdd && !_.isEmpty(manager.projectUsersToAdd.selected)) {
                    await AddUser_createTaskUsers(manager.task, manager.projectUsersToAdd)
                }
            } else {
                if (!_.isEmpty(manager.companyUsersToAdd.selected)) {
                    await AddUser_createProjectUsers(manager.project, manager.companyUsersToAdd)
                    await AddUser_createTaskUsers(manager.task, manager.companyUsersToAdd)
                }

                if (manager.projectUsersToAdd && !_.isEmpty(manager.projectUsersToAdd.selected)) {
                    await AddUser_createTaskUsers(manager.task, manager.projectUsersToAdd)
                }
            }
            break

        case AddUserType.CHAT_USER:

            if (manager.workspace) {
                if (!_.isEmpty(manager.companyUsersToAdd.selected)) {
                    // here we need create workspace users and then project users
                    await AddUser_createWorkspaceUsers(manager.workspace, manager.companyUsersToAdd)
                    await AddUser_createProjectUsers(manager.project, manager.companyUsersToAdd)
                    await AddUser_createChatUsers(manager.chat, manager.companyUsersToAdd)
                }

                if (manager.workspaceUsersToAdd && !_.isEmpty(manager.workspaceUsersToAdd.selected)) {
                    await AddUser_createProjectUsers(manager.project, manager.workspaceUsersToAdd)
                    await AddUser_createChatUsers(manager.chat, manager.workspaceUsersToAdd)
                }

                if (manager.projectUsersToAdd && !_.isEmpty(manager.projectUsersToAdd.selected)) {
                    await AddUser_createChatUsers(manager.chat, manager.projectUsersToAdd)
                }
            } else {
                if (!_.isEmpty(manager.companyUsersToAdd.selected)) {
                    await AddUser_createProjectUsers(manager.project, manager.companyUsersToAdd)
                    await AddUser_createChatUsers(manager.chat, manager.companyUsersToAdd)
                }

                if (manager.projectUsersToAdd && !_.isEmpty(manager.projectUsersToAdd.selected)) {
                    await AddUser_createChatUsers(manager.chat, manager.projectUsersToAdd)
                }
            }
            break

        default:
            throw Error("#9484jjjj Not implemented")
    }
}

export async function AddUser_createWorkspaceUsers(workspace?: Workspace, usersToAdd?: UsersToAdd) {
    if (workspace && usersToAdd) {
        await ApiProjectUser.createWorkspaceUsers(workspace.id, usersToAdd.selected).promise()
    }
}

export async function AddUser_createProjectUsers(project?: ProjectSession, usersToAdd?: UsersToAdd) {
    if (project && usersToAdd) {
        await ApiProjectUser.createProjectUsers(project.id, usersToAdd.selected).promise()
    }
}

export async function AddUser_createTaskUsers(task?: Task, usersToAdd?: UsersToAdd) {
    if (task?.id && usersToAdd) {
        await ApiTask.createTaskUsers(task.id, usersToAdd.selected).promise()
    }
}

export async function AddUser_createChatUsers(chat?: Chat, usersToAdd?: UsersToAdd) {
    if (chat?.id && usersToAdd) {
        await ApiChat.createChatUsers(chat.id, usersToAdd.selected).promise()
    }
}

type BulkUserGrant = {
    userId               : ID
    workspaceProjectIds? : ID[]
    workspaceAccess?     : ProjectAccess
    projectIds?          : ID[]
    projectAccess?       : ProjectAccess
}

async function AddUser_createBulkTaskUsers(manager: AddUser_Manager): Promise<void> {
    const tasks = manager.tasks
    if (!tasks || !tasks.length) return

    const selected: UserToAdd[] = _.compact([
        ...manager.companyUsersToAdd.selected,
        ...manager.workspaceUsersToAdd.selected,
        ...manager.projectUsersToAdd.selected,
    ])

    if (!selected.length) return

    const taskIds = _.map(tasks, t => t.id)

    // One row per selected user, carrying the workspaces / sub-projects
    // that user is still missing from. Users already fully-provisioned
    // ship an empty-lists row -- the userId alone tells the backend to
    // create task_user rows for them.
    const users: BulkUserGrant[] = _.map(selected, u => _bulkGrantForUser(tasks, u))

    await ApiBulkActions.addUsersToTasks(taskIds, users).promise()
}

// Build the /bulk/tasks/users/add row for ONE selected user across the
// selected tasks.
//
// For every task the user is being added to, we look at:
//   - the task's workspace (root project of the workspace it lives in)
//   - the task's own project
// and collect the ones this user is NOT already a member of. Backend
// needs to grant workspace / sub-project access BEFORE writing the
// task_user row -- otherwise the task_user row would dangle (no matching
// project_user).
//
// Returns one BulkUserGrant with two dedup'd lists: workspaceProjectIds
// (missing workspaces) and projectIds (missing sub-projects). Same
// workspaceAccess applies to every id in workspaceProjectIds; same
// projectAccess applies to every id in projectIds. Both lists may be
// empty -- that's fine, the backend still creates task_user rows for
// this user.
function _bulkGrantForUser(tasks: Task[], userToAdd: UserToAdd): BulkUserGrant {

    // Access levels the caller picked for THIS user in the modal (STANDARD
    // fallback if the modal didn't collect one).
    const workspaceAccess = userToAdd.workspaceAccess ?? ProjectAccess.STANDARD
    const projectAccess   = userToAdd.projectAccess   ?? ProjectAccess.STANDARD

    // Sets, so the same workspace/project referenced by many tasks is only
    // recorded once per user.
    const workspaceSet : Set<ID> = new Set()
    const projectSet   : Set<ID> = new Set()

    // Scan every task and record the workspace + project the user is
    // missing from. projectUsers lookup uses the CURRENT client-side
    // session cache — good enough because the backend re-checks.
    _.each(tasks, (task: Task) => {
        const project = task.defaultProject
        if (!project) return

        const workspaceProjectId = project.workspace?.project.id
        if (workspaceProjectId
            && !gd.session.projectUsers.findByProjectAndUser(workspaceProjectId, userToAdd.id)) {
            workspaceSet.add(workspaceProjectId)
        }

        if (!gd.session.projectUsers.findByProjectAndUser(project.id, userToAdd.id)) {
            projectSet.add(project.id)
        }
    })

    return {
        userId              : userToAdd.id,
        workspaceProjectIds : Array.from(workspaceSet),
        workspaceAccess,
        projectIds          : Array.from(projectSet),
        projectAccess,
    }
}